import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { useBlocker } from '@tanstack/react-router';
import { Slot } from 'radix-ui';
import type { ControllerProps, FieldPath, FieldValues, SubmitErrorHandler, UseFormHandleSubmit } from 'react-hook-form';
import { Controller, FormProvider } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { noop, snackbar, useBoolean, usePrevious } from '@/_shared';
import type { DivProps } from '@/types';

import type { SubFormLinkHandler, SubFormUnlinkHandler } from './context';
import { MainFormProvider, useMainFormContext } from './context';
import { FormFieldContext } from './form-field-context';
import { FormItemContext } from './form-item-context';
import { MoveAwayConfirmationModal } from './move-away-confirmation-modal';
import type { FormProps, SubFormProps } from './types';
import { useFormField } from './use-form-field';

import { HelperText } from '../../typography/helper-text';
import { Label } from '../../typography/label';
import type { LabelProps } from '../../typography/types';

function Form<T extends FieldValues>({
  children,
  allowUnsavedChanges = false,
  isSubmitting: isSubmittingForm = false,
  isSuccess = false,
  isEdit = false,
  ...formProps
}: FormProps<T>) {
  const {
    formState: { isDirty: isMainFormDirty },
  } = formProps;

  const prevSubmittingForm = usePrevious(isSubmittingForm);
  const [isSubmitting, submitSubForm] = useBoolean();
  const [subForms, setSubForms] = useState<Record<string, Omit<SubFormProps<T>, 'id'>>>({});

  const dirtySubFormIds = useMemo(
    () => Object.keys(subForms).filter((key) => subForms[key].formState.isDirty),
    [subForms]
  );

  const isSubFormDirty = dirtySubFormIds.length > 0;
  const isFormDirty = isMainFormDirty || isSubFormDirty;
  const preventNavigation =
    isEdit &&
    !allowUnsavedChanges &&
    ((!isSubmittingForm && !prevSubmittingForm) || (prevSubmittingForm && !isSubmittingForm && !isSuccess)) &&
    isFormDirty;

  const blocker = useBlocker({
    shouldBlockFn: () => !!preventNavigation,
    enableBeforeUnload: () => !!preventNavigation,
    withResolver: true,
  });

  const shouldBlockNavigation = blocker.status === 'blocked';

  // console.log('MAIN FORM CONTEXT', {
  //   subForms,
  //   isSuccess,
  //   isSubmittingForm,
  //   prevSubmittingForm,
  //   mainFormDirty: isMainFormDirty,
  //   isFormDirty,
  //   dirtySubFormIds,
  //   preventNavigation,
  //   shouldBlockNavigation,
  // });

  const linkSubForm: SubFormLinkHandler<T> = useCallback((id, control) => {
    setSubForms((formsMap) => ({
      ...formsMap,
      [id]: control,
    }));
  }, []);

  const unlinkSubForm: SubFormUnlinkHandler = useCallback((id) => {
    setSubForms((formsMap) => {
      const { [id]: _, ...rest } = formsMap;
      return rest;
    });
  }, []);

  const validateAllSubForms = useCallback(async () => {
    const subFormsValidArr: Promise<boolean>[] = [];
    Object.values(subForms).forEach((subForm) => {
      if (subForm.formState.isDirty) {
        subFormsValidArr.push(subForm.trigger());
      }
    });

    const allSubFormsValid = await Promise.all(subFormsValidArr);

    return !allSubFormsValid.includes(false);
  }, [subForms]);

  const validateAllForms = useCallback(async () => {
    const allSubFormsValid = await validateAllSubForms();
    if (!allSubFormsValid) {
      return false;
    }
    const mainFormValid = await formProps.trigger();
    return mainFormValid;
  }, [formProps, validateAllSubForms]);

  const submitAllSubForms = useCallback(async () => {
    const submissions: Promise<boolean | undefined>[] = [];
    Object.keys(subForms).forEach((key) => {
      const subForm = subForms[key];
      // Submit only those SubForms that are dirty
      if (subForm.formState.isDirty && typeof subForm.onSubmit === 'function') {
        // Need onSubmit instead of using form.trigger() because it doesn't work with async validation
        submissions.push(subForm.onSubmit(subForm.getValues()));
      }
    });
    return Promise.all(submissions);
  }, [subForms]);

  // Capture the page's onValid callback so submitForm() can replay it programmatically
  const latestOnValidRef = useRef<((data: T) => void) | null>(null);

  const handleSubmit: UseFormHandleSubmit<T> = useCallback(
    (onValid, onInvalid) => {
      latestOnValidRef.current = onValid;

      // Handle SubForms submissions
      const onValidAllForms = async () => {
        // Validate All Forms
        const allFormsValid = await validateAllForms();
        if (!allFormsValid) {
          snackbar.error('Please fix all errors.', { toastId: 'FORM_VALIDATION_ERROR' });
          return;
        }

        // Submit all SubForms
        await submitAllSubForms();
        // Submit main form
        onValid(formProps.getValues());
      };

      const onInValidAllForms: SubmitErrorHandler<T> = async (e) => {
        snackbar.error('Please fix all errors.', { toastId: 'FORM_VALIDATION_ERROR' });
        console.error('Form validation error', e);
        onInvalid?.(e);
      };

      return formProps.handleSubmit(onValidAllForms, onInValidAllForms);
    },
    [formProps, submitAllSubForms, validateAllForms]
  );

  const submitForm = useCallback(() => {
    if (!latestOnValidRef.current) return;
    handleSubmit(latestOnValidRef.current)();
  }, [handleSubmit]);

  const proceedNavigation = useCallback(() => {
    formProps.reset();
    blocker.proceed?.();
  }, [blocker, formProps]);

  const value = useMemo(
    () => ({
      isFormDirty,
      dirtySubFormIds,
      isSubmitting: isSubmitting || isSubmittingForm,
      submitSubForm,
      linkSubForm,
      unlinkSubForm,
      submitForm,
    }),
    [
      dirtySubFormIds,
      isFormDirty,
      isSubmitting,
      isSubmittingForm,
      linkSubForm,
      submitSubForm,
      unlinkSubForm,
      submitForm,
    ]
  );

  return (
    <MainFormProvider value={value}>
      <MoveAwayConfirmationModal
        open={shouldBlockNavigation}
        onClose={blocker.reset || noop}
        onProceed={proceedNavigation}
        onCancel={blocker.reset || noop}
      />
      <FormProvider {...formProps} handleSubmit={handleSubmit}>
        {typeof children === 'function' ? children({ handleSubmit }) : children}
      </FormProvider>
    </MainFormProvider>
  );
}

function SubForm<T extends FieldValues>({ id, children, ...formProps }: SubFormProps<T>) {
  const mainFromContext = useMainFormContext();

  if (!mainFromContext) {
    throw new Error('SubForm must be used within <Form>');
  }

  const { submitSubForm, linkSubForm, unlinkSubForm } = mainFromContext;

  const { isSubmitting = false } = formProps;

  useEffect(() => {
    (linkSubForm as (id: string, subForm: unknown) => void)(id, formProps);

    return () => {
      unlinkSubForm(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, formProps.formState.isDirty]);

  useEffect(() => {
    submitSubForm.set(isSubmitting);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting]);

  return <FormProvider {...formProps}>{children}</FormProvider>;
}

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

function FormItem({ className, ...props }: DivProps) {
  const id = useId();
  return (
    <FormItemContext.Provider value={{ id }}>
      <div data-slot="form-item" className={twMerge('space-y-1.5', className)} {...props} />
    </FormItemContext.Provider>
  );
}

function FormLabel({ className, ...props }: LabelProps) {
  const { error, formItemId } = useFormField();
  return (
    <Label
      data-slot="form-label"
      data-error={!!error}
      className={twMerge('data-[error=true]:text-destructive', className)}
      htmlFor={formItemId}
      {...props}
    />
  );
}
function FormControl({ ...props }: React.ComponentProps<typeof Slot.Root>) {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
  return (
    <Slot.Root
      data-slot="form-control"
      id={formItemId}
      aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
      aria-invalid={!!error}
      {...props}
    />
  );
}

function FormDescription({ className, ...props }: React.ComponentProps<'p'>) {
  const { formDescriptionId } = useFormField();
  return (
    <p
      data-slot="form-description"
      id={formDescriptionId}
      className={twMerge('text-c-card-muted text-xs font-normal', className)}
      {...props}
    />
  );
}

const FormMessage = ({
  className,
  id,
  children,
  title,
  forceHeight = true,
  ...props
}: DivProps & { forceHeight?: boolean }) => {
  const { error, formMessageId } = useFormField();

  let body = error ? String(error?.message) : children;

  if (id) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    body = error?.[id]?.message ?? '';
  }

  if (typeof body === 'string' && body.indexOf('{FIELD_NAME}') > -1) {
    body = body.replace('{FIELD_NAME}', (title ?? '').replace('(required)', '').trim());
  }

  if (typeof error === 'object' && body === 'undefined') {
    body = 'Invalid ' + Object.keys(error).join(', ');
  }

  return (
    <HelperText
      id={formMessageId}
      className={twMerge('text-error text-xs', forceHeight && 'min-h-4', !forceHeight && !body && 'hidden', className)}
      {...props}
    >
      {body}
    </HelperText>
  );
};

export { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, SubForm };
